/**
 * [需求⑯ 定时任务·内容日历] Content Calendar / Content Pool / 单条详情弹窗共用的数据契约与 mock。
 *
 * 纯前端 mock（2026-08-19;2026-08-22 改诚实:分平台文案、带图、不捏造失败 ——
 * 之前同文双平台 + 假 failed 行被误判成"生成逻辑坏了"）。真实数据来自「后端排期表 + agent 批量生成产物」，前端只负责渲染与
 * 交互回填。后端 / agent 怎么接（详见 /tmp Maya3.0 前端改动清单 ⑯ 段）：
 *   - 列表   GET   /marketing/calendar?month=YYYY-MM        → CalendarItem[]（date=null 即在 Content Pool）
 *   - 改期   PATCH /marketing/calendar/{id}                 { date, time }  ← 拖拽落日 / 弹窗改期下拉都走它
 *   - 取消排期 PATCH /marketing/calendar/{id}               { date:null }   ← 日历 chip 上的 × / 拖回 Pool
 *   - Retry  POST  /marketing/calendar/{id}/versions/{platform}/retry       ← 失败版本重跑（任务中心也可点）
 *   - 重生成  POST  /marketing/calendar/{id}/versions/{platform}/regenerate  { note? }
 *   - 存编辑  PATCH /marketing/calendar/{id}/versions/{platform}             { title, body }
 *   - 立即发  POST  /marketing/calendar/{id}/publish
 *   - 丢弃   DELETE /marketing/calendar/{id}
 *   - Pause All POST /marketing/calendar/pause-all          { paused }      ← Auto-Publish On ↔ Paused
 *
 * 图标复用 QuickSetupForm 同源资产（⑮ Publish To 用的也是这套），保证品牌图一致。
 */

import { withHiddenAgentNote } from '../../../lib/services/agents/hiddenAgentNote';
import ShopifyIcon from '../../../assets/maya-icons/shopify.svg';
import FacebookIcon from '../../../assets/maya-icons/facebook.svg';
// 演示 hero 图(真实层 = 生成产物自带的配图 URL;字段契约见 PlatformVersion.imageUrl)。
import DemoHero from '../../../assets/maya-product-serum.png';
// 品牌色 IG / X 走 public 资产（maya-icons 里的是纯白版，日历要彩色）。
const InstagramIcon = '/images/composio/instagram.svg';

/** 详情弹窗里出现的三个平台（Figma：Shopify Blog / Instagram / X）。 */
export type PlatformKey = 'shopify_blog' | 'instagram' | 'facebook';

export interface PlatformMeta {
  key: PlatformKey;
  label: string;
  icon: string;
}

export const CALENDAR_PLATFORMS: PlatformMeta[] = [
  { key: 'shopify_blog', label: 'Shopify Blog', icon: ShopifyIcon },
  { key: 'instagram', label: 'Instagram', icon: InstagramIcon },
  { key: 'facebook', label: 'Facebook', icon: FacebookIcon },
];

/** 日内排序:按 time("HH:MM",两侧都已补零)升序;无时间的排最后,同时间保持稳定。 */
export function byTimeAscending(a: { time?: string }, b: { time?: string }): number {
  return (a.time || '99:99').localeCompare(b.time || '99:99');
}

export function platformMeta(key: PlatformKey): PlatformMeta {
  return CALENDAR_PLATFORMS.find(p => p.key === key) ?? CALENDAR_PLATFORMS[0];
}

/** 单个平台的那一版内容。 */
export interface PlatformVersion {
  platform: PlatformKey;
  title: string;
  body: string;
  /** 该平台字数上限（IG=2200；博客无上限则缺省 —— 编辑态只有设了才显示 "x / limit"）。 */
  charLimit?: number;
  /** 该版本的配图（生成产物的 hero/社媒图）。真实层必填 —— 没图的 IG 版本发布会被拒。 */
  imageUrl?: string;
  status: 'ok' | 'failed';
  /** status==='failed' 时展示在琥珀框里的原因。 */
  failReason?: string;
  /**
   * 哪一步失败的(PRD #16 ②③)。两张红卡的 Retry 是**两个不同动作**:
   * 'generate' → 重跑 writer(跳 Maya Chat 重做这一版);
   * 'publish'  → 内容是好的,重新往渠道发一次(走直发端点,不经过模型)。
   * status='failed' 分不出来,所以后端 (calendar_versions.fail_stage) 带上来。
   * 缺省按 'generate' 处理 —— 老数据只可能是生成失败,发布失败链路 2026-08-26 才有。
   */
  failStage?: 'generate' | 'publish';
  /** agent 草稿 id(存储行带回)——Publish Now 直发与回填记录都用它。 */
  draftId?: string;
  /**
   * 这一版发出去之后在平台上的原帖地址(IG permalink / FB post / 博客文章)。
   * 只有已发布的版本有;来源是 chatbot-api 的发布记录(`published_content.external_url`)——
   * yepairag 的日历存储不返回它,所以存储行是靠 draftId 从发布记录借过来的
   * (见 mergeCalendarSources)。拿不到就没有,详情弹窗不显示「View post」。
   */
  externalUrl?: string;
}

/** 一条排期任务（= 一篇内容，含多平台版本）。 */
export interface CalendarItem {
  id: string;
  /**
   * 批次身份：一次生成任务（一次 CREATE_CONTENT_CALENDAR）产出的所有条目共享同一个
   * plan_id。Content Pool 靠它分组 —— **不要用 title 分组**：一次任务生成的几篇
   * 本来标题就各不相同，按标题分只会把一批拆散。
   * undefined = 不属于任何批次（聊天里的单篇、发布回填），归入 Individual 组。
   */
  planId?: string;
  /** 批次名（后端 marketing.plans.label）；缺省时组标题回落成 "Batch"。 */
  planLabel?: string;
  /**
   * 篇目在批次里的位置（0 起）。批次卡内部按它排 —— 一个计划是在一个事务里提交的，
   * 所有行的 createdAt 一模一样（dev 实测：八篇的批次八行同一个微秒），
   * 时间戳在这里帮不上任何忙。老批次没有这个值 → 保持原序。
   */
  planSeq?: number;
  /** 规范标题（各平台可用 version.title 覆写）。 */
  title: string;
  /** 'YYYY-MM-DD'；null = 未排期，落在 Content Pool。 */
  date: string | null;
  /** 'HH:mm'（24 小时）。 */
  time: string;
  /** 第几次生成，展示 "gen #N"。 */
  genCount: number;
  /** setup 摘要标签："Custom Setup • 1 products • 2 keywords"。 */
  setup: { label: string; products: number; keywords: number };
  /** 挂的 campaign / 折扣码（可选，展示第二枚蓝标签）。 */
  campaign?: { code: string; label: string };
  versions: PlatformVersion[];
  /**
   * 这一篇最后一次**写出内容**的时刻（ISO，UTC）——Content Pool 的排序键。
   * 批次的行是提交计划那一秒批量建出来的，所以 createdAt 只说明"什么时候排的计划"：
   * 昨天排的计划、今天写出来的稿子，按 createdAt 排会沉到昨天的位置去。
   */
  contentAt?: string;
  /**
   * 行自己的创建时刻（ISO，UTC）。批次**组内**按它升序 = 计划里的篇目顺序；
   * 也是 contentAt 缺失（老数据、聊天草稿）时的回退排序键。
   */
  createdAt?: string;
  /** 存储行的整体态:published = 已发布(置灰、不可拖、不可点、不可再 Publish Now)。
   *  [评论·Ronny 2026-08-26] publishing = 立即发布进行中(琥珀态,逐渠道异步):不可拖、无 ×、
   *  不被批量清理带走;发完才转 published。本地态,由 publishNowJob 驱动。 */
  status?: 'scheduled' | 'publishing' | 'published';
}

/** 条目整体态：任一平台版本失败 → 'failed'；否则 'scheduled'。 */
export function itemStatus(item: CalendarItem): 'scheduled' | 'failed' {
  return item.versions.some(v => v.status === 'failed') ? 'failed' : 'scheduled';
}

/**
 * 日历红卡上的那行字(PRD #16 ②③)。
 *
 * 「没发出去」和「没写出来」对商家是两件事:前者内容是好的、重发一次就行,后者要重跑 writer。
 * 一条内容多平台时只要有一个是发布失败就按发布失败说 —— 那是更紧急的那个(内容已就绪却没上线)。
 */
export function failedLabel(item: CalendarItem): string {
  const failedVersions = item.versions.filter(v => v.status === 'failed');
  return failedVersions.some(v => v.failStage === 'publish')
    ? 'Publish failed…'
    : 'Generation failed…';
}

/** Content Pool 的一个批次分组。 */
export interface PoolGroup {
  /** 批次 id；null = "Individual" 组（聊天单篇等无批次条目的收容组）。 */
  planId: string | null;
  /** 组标题。 */
  label: string;
  items: CalendarItem[];
}

/**
 * 一条内容在 Content Pool 里的排序时刻（缺时间的老数据排到最后，不是最前）。
 * contentAt 优先：批次的行在提交计划那一秒就建好了，createdAt 排的是"什么时候
 * 排的计划"，不是"什么时候写出来的"。
 */
export function poolSortKey(it: CalendarItem): number {
  const raw = it.contentAt ?? it.createdAt;
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(t) ? -Infinity : t;
}

/** Content Pool 的排序：newest first（列表上方一直是这么写的）。 */
export function sortPoolNewestFirst(pool: CalendarItem[]): CalendarItem[] {
  return [...pool].sort((a, b) => poolSortKey(b) - poolSortKey(a));
}

/**
 * Content Pool 按批次分组（2026-08-27）。
 *
 * 分组键是 planId，**不是 title** —— 一次任务生成的三篇文章标题本来就各不相同，
 * 按标题分只会把一批拆成三张互不相干的卡片（这正是改之前的表现）。
 * 组标题取后端的 plans.label，不需要额外跑一次 LLM 去 summary 正文：
 * 这个 label 在任务提交时就已经写进去了。
 *
 * 顺序（2026-08-30 起）：
 *   - 组间按传入顺序（调用方已 sortPoolNewestFirst），即每组最新的那条决定组的位置。
 *     **Individual 组不再固定垫底** —— 它垫底时，刚在聊天里写出来的单篇会被十几行
 *     早就排好的批次压住，"newest first"在有批次的池子里从来没成立过。
 *   - 批次**组内**按 planSeq 升序 = 计划里的篇目顺序。组内也跟着 newest first 的话，
 *     一个批次的几篇会按写完的先后倒着排，读起来是乱的（它们之间的先后只有计划顺序
 *     这一个有意义的解释）。**不能用 createdAt 代替**：一个计划是在一个事务里提交的，
 *     所有行的 created_at 是同一个微秒（dev 实测），排了等于没排。
 * 一条都没有时不产生空组。
 */
export function groupPoolByBatch(pool: CalendarItem[]): PoolGroup[] {
  const groups: PoolGroup[] = [];
  const byPlan = new Map<string, PoolGroup>();
  let loose: PoolGroup | null = null;
  for (const it of pool) {
    if (!it.planId) {
      if (!loose) {
        loose = { planId: null, label: 'Individual', items: [] };
        groups.push(loose);
      }
      loose.items.push(it);
      continue;
    }
    let g = byPlan.get(it.planId);
    if (!g) {
      g = { planId: it.planId, label: it.planLabel || 'Batch', items: [] };
      byPlan.set(it.planId, g);
      groups.push(g);
    }
    g.items.push(it);
  }
  for (const g of groups) {
    // 老批次(2026-08-30 之前提交的)没有 planSeq —— 保持原序,别拿 createdAt 假排。
    if (g.planId === null || g.items.some(i => i.planSeq == null)) continue;
    g.items.sort((a, b) => (a.planSeq as number) - (b.planSeq as number));
  }
  return groups;
}

/**
 * 「重做这一个渠道版本」发给 Maya 的消息（2026-08-27）。
 *
 * 生成失败没法像发布失败那样零 LLM 直接重跑 —— 正文可能根本不存在，只能跳 Chat 重写。
 * 但跳过去之后，这次重写是不是"只重做这一个"、写完能不能回到原来那格，就全靠这条消息
 * 说清楚了；此前它只有一句 "redo that version."，三件事一件都没说：
 *
 *   ① 只重做这一个渠道 —— 没说，agent 可能顺手把整篇三个渠道都重写一遍；
 *   ② task_id 要用 planId —— 没说，重写出来的稿子会走 file_pool_item 落进 Content Pool，
 *      原来那行仍然是红的：商家点了 Retry，红卡不消失，池子里反而多一份；
 *   ③ 复用原标题 —— 没说，bind_draft 在同渠道内是按标题认行的（失败行 status='failed'
 *      但没被删，仍占着这个渠道的位置），标题一变就会顺延到下一行，绑到别人的格子里。
 *
 * 技术细节走 hidden agent note：agent 读得到，商家的气泡里不出现。
 */
/** [PRD #16 Retry · 2026-08-28] 重做失败版本的消息。
 *
 *  渠道**不在这句话里**:它连同这一行的身份、原标题、排期一起冻在服务端的重做票
 *  上(openRedoTicket),CREATE_CONTENT_DRAFT 凭 token 把它们读回去。这里只剩两样
 *  东西 —— 商家看得懂的那句人话(失败原因 + 他自己写的要求),和一个不可解释的
 *  token。2026-08-27 的教训:把渠道写成给模型的指令,它就只是措辞 —— 重做 IG 版
 *  回来一篇 blog,而服务端按模型给的渠道筛行,于是一行都没筛到,整篇掉进池子。
 */
export function redoVersionMessage(
  // [3.5 #9] 入参放宽到真正读到的那几个字段(原来要求整个 CalendarItem / PlatformVersion)。
  // 聊天里的批量完成卡也要发这条消息,它手上只有日历存储行,拼不出完整的 CalendarItem;
  // 而这条措辞必须只有一份 —— 「三个改稿入口共用一份措辞」是这个文件既有的铁律,
  // 现在是四个。所有既有调用方不受影响(传整个对象照样满足 Pick)。
  item: Pick<CalendarItem, 'title'>,
  platform: PlatformKey,
  version: Pick<PlatformVersion, 'failReason' | 'failStage'> | undefined,
  note?: string
): string {
  return failedVersionMessage({
    title: item.title,
    platform,
    failReason: version?.failReason ?? null,
    failStage: version?.failStage ?? null,
    note,
  });
}

/**
 * 同一句话的**原始字段版**,给手上没有 CalendarItem/PlatformVersion 的调用方
 * (批量完成卡的单条 Retry 就是:它拿到的是 plan_outcome 的一条 piece)。
 *
 * 存在的理由是措辞只能有一份 —— 这个文件已经因为「同一句话抄成两份」栽过一次
 * (见下方 draftRewriteInstruction 的注释)。失败重试和商家自己发起的改稿是**两件事**:
 * 前者商家没有「想改什么」可说,那篇根本没写出来,再问一遍就是把重试变成一轮问答;
 * 后者才走「没说清就先问」那套 —— 那是 `redoRequestMessage` 的事,不是这里的。
 */
export function failedVersionMessage(args: {
  title: string;
  platform: PlatformKey;
  failReason: string | null;
  failStage: 'generate' | 'publish' | null;
  note?: string;
}): string {
  const label = platformMeta(args.platform).label;
  const title = args.title?.trim() || 'this post';
  const reason = args.failReason?.trim();
  const trimmedNote = args.note?.trim();
  // 哪一步失败,说的就得是哪一步。发布失败也会走到这条路(平台嫌文案本身时,
  // 红卡的主动作是 Regenerate),而那篇稿子是写出来了的 —— 告诉 Maya「没写出来」
  // 会让她按「重写一篇」去做,漏掉真正要解决的事:这份文案被渠道退了回来。
  const opening =
    args.failStage === 'publish'
      ? `The ${label} version of "${title}" was written, but ${label} turned it down` +
        ' — please rewrite it so it can go out.'
      : `The ${label} version of "${title}" failed to generate — please redo it.`;
  const visible = [
    opening,
    reason ? `What went wrong: ${reason}` : null,
    trimmedNote ? `What I want changed: ${trimmedNote}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  // 失败重试**永远**直接重写,不先问一句 —— 这里没有 pending 那一支(2026-09-16)。
  //
  // 曾经的判据是「连失败原因带批注都没有 → 先问商家想改什么」,理由听起来成立:
  // 什么线索都没有,总得问点什么。dev 实测(kiechee.pau,2026-09-16)证明它站不住:
  //
  //   ① 「没有失败原因」不是罕见情形,而是最常见那一类的**必然**。批量完成卡的失败
  //      篇目是 plan_outcome 用「行声明的渠道 − 真落了稿的渠道」推出来的
  //      (marketing_calendar_store.plan_outcome),压根没生成出来的那一篇没人记录过
  //      原因,fail_reason 恒为 None。于是完成卡上的 Retry 100% 落进先问那一支。
  //   ② 就算问了,商家也答不上来 —— 那篇稿子不存在,他没有「想改什么」可说。他按
  //      Retry 要的是重跑 writer,换来的却是一轮问答,而进度条那侧已经打了绿勾。
  //
  // 「没说清就先问」是**商家主动改稿**才成立的规矩(redoRequestMessage / 两条
  // draftRewrite·planRewrite 指令),那时确实有东西可问。失败重试不是改稿。
  return withHiddenAgentNote(visible, redoTicketInstruction(false));
}

/** 服务端说的渠道键 → 这个文件用的 PlatformKey。 */
export function platformKeyOfChannel(channel: string): PlatformKey {
  const c = (channel || '').toLowerCase();
  if (c.includes('instagram')) return 'instagram';
  if (c.includes('facebook')) return 'facebook';
  return 'shopify_blog';
}

/** 那条隐藏指令的唯一出处 —— 三个改稿入口共用一份措辞。
 *  它只说「把这个 token 交给 CREATE_CONTENT_DRAFT」:哪一篇、哪个渠道、落回哪一行,
 *  服务端从票里读,模型无从改写(这就是「硬」和「提示词」的区别)。 */
export function redoTicketInstruction(pending = false, channelLabel?: string): string {
  // The channel is the one thing the model must know BEFORE writing: it decides which writer
  // skill the copy follows. The ticket still owns the row; this only says which version.
  const channelLine = channelLabel
    ? ` This redo is the ${channelLabel} — write it with that channel's writer rules, keep its title, and do not produce any other channel's version.`
    : '';
  // 这条指令里**没有任何值要模型转抄**(Monica 2026-08-30 的铁律)。哪一篇、哪个
  // 渠道、落回哪一行,服务端按这段对话自己认领(open 一张票时 Hub 就写下了对话)。
  // 之前这里带着 redo_ticket="…",模型把它抄短两位,整次重做就白做且不报错。
  if (pending) {
    return (
      'The merchant pressed Regenerate without saying what to change. Do NOT write' +
      " anything yet: ask what they want changed exactly as this task's revise rules" +
      ' require, wait for their answer, and only then write the piece. The server' +
      ' already knows which piece, which channel and which calendar row this redo' +
      ' belongs to — do not restate the channel and do not start a new piece. When' +
      ' you do write it, this channel needs its image: generate one for this redo.' +
      channelLine
    );
  }
  return (
    'Rewrite this piece and call CREATE_CONTENT_DRAFT with the new copy only. The' +
    ' server already knows which piece, which channel and which calendar row this' +
    ' redo belongs to — do not restate the channel, do not change the title, and do' +
    ' not start a new piece. This channel needs its image: generate one for this redo.' +
    channelLine
  );
}

/** 没有票时那条指令 —— chat 草稿卡专用(这一稿可能从没落到任何一行,票开不出来
 *  是正常结局,不是故障),身份只能靠标题说。
 *
 *  它必须和 `redoTicketInstruction` 一样分两种。2026-08-28 dev 实测的教训:无票那条
 *  老措辞只说「去重写」,和 task 的 revise 规则(没说改什么就先问一次)正好相反,
 *  模型跟着指令走 —— 一句没问就重写了一篇;think 里还看得到她因为「指令没让我问」
 *  而跑去自己列草稿猜要改哪篇。措辞打架比措辞缺失更坏。 */
export function draftRewriteInstruction(title: string, pending = false): string {
  const identity =
    `the existing draft titled "${title}" — treat as a whole-post rewrite (all` +
    ' platform versions of this task), keep the same task, and supersede the' +
    ' previous version.';
  // 问什么、怎么问由 task 的 revise 规则定(那句固定问法),这里只说「先问」——
  // 措辞抄成两份必然漂移,这是 redoTicketInstruction 立下的规矩。
  if (pending) {
    return (
      'The merchant pressed Regenerate without saying what to change. Do NOT write' +
      " anything yet: ask what they want changed exactly as this task's revise rules" +
      ' require, wait for their answer, and only then rewrite. What you will be' +
      ` rewriting is ${identity}`
    );
  }
  return `Regenerate ${identity}`;
}

/** 无票、但知道自己属于哪个批次时的指令(日历弹窗的 Regenerate in chat 退路)。
 *
 *  和 `draftRewriteInstruction` 同理,只是身份换成计划行:落位靠 task_id,不靠标题。 */
export function planRewriteInstruction(pending = false): string {
  // 这里**没有任何值要模型转抄**(2026-08-30 铁律)。这一篇属于哪个批次、落回哪一行,
  // 由服务端按这段对话认领(active_plan_for_conversation);以前这条写着
  // `Pass task_id="plan_…"`,那正是抄错就散批的老路。
  const landing =
    'This post belongs to a calendar plan the server already knows about — rewrite it' +
    ' in place; do not start a new piece and do not restate any id.';
  if (pending) {
    return (
      'The merchant pressed Regenerate without saying what to change. Do NOT write' +
      " anything yet: ask what they want changed exactly as this task's revise rules" +
      ` require, wait for their answer, and only then rewrite. ${landing}`
    );
  }
  return landing;
}

/** 不是失败重试的改稿(弹窗的 Regenerate in chat、chat 草稿卡的 Regenerate):
 *  可见的就是商家自己那句话,身份同样由票据担保。 */
/** How a channel is named to Maya in a redo message. Measured on dev 2026-09-08: told only
 *  "Regenerate this post" with the channel left to the ticket, the model wrote a 419-character
 *  Instagram caption and the server filed it, channel corrected, on the BLOG row. The ticket
 *  can fix the channel after the fact; only the message can fix it before the words are written. */
const REDO_CHANNEL_LABEL: Record<string, string> = {
  blog: 'Shopify blog article',
  shopify: 'Shopify blog article',
  instagram: 'Instagram post',
  facebook: 'Facebook post',
  x: 'X post',
};

export function redoRequestMessage(title: string, note?: string, channel?: string): string {
  const trimmed = note?.trim();
  const label = channel ? REDO_CHANNEL_LABEL[channel.toLowerCase()] : undefined;
  const which = label ? `the ${label} version of "${title}"` : `"${title}"`;
  const visible = trimmed ? `Regenerate ${which} — ${trimmed}` : `Regenerate ${which}`;
  // 空批注 = 他还没说要改什么(弹窗那行小字就是这么承诺的:留空她会先问哪里不对)。
  return withHiddenAgentNote(visible, redoTicketInstruction(!trimmed, label));
}

/** setup 标签文案："Custom Setup • 1 products • 2 keywords"。 */
export function setupLabel(item: CalendarItem): string {
  return `${item.setup.label} • ${item.setup.products} products • ${item.setup.keywords} keywords`;
}

// ---- mock 生成小工具 ----------------------------------------------------------

/** 组一条内容的双平台版本。每个平台一份**各自风格**的文案 —— 真实生成层就是这么产出的
 *  (writer-blog / writer-instagram 分渠道写),mock 假装成同一段正文会让人误判生成逻辑坏了
 *  (2026-08-22 实测就被误判了一次)。失败状态不再捏造:真实层由生成结果带 status,
 *  mock 一律 'ok' —— 日历上看到 failed 必须意味着真的失败。 */
function mkVersions(v: {
  blogTitle: string;
  blog: string;
  igTitle: string;
  ig: string;
  imageUrl?: string;
}): PlatformVersion[] {
  return [
    {
      platform: 'shopify_blog',
      title: v.blogTitle,
      body: v.blog,
      // 博客无字数上限 —— 之前统一塞 280 只是对齐 Figma 截图的偷懒。
      status: 'ok',
      imageUrl: v.imageUrl ?? DemoHero,
    },
    {
      platform: 'instagram',
      title: v.igTitle,
      body: v.ig,
      charLimit: 2200,
      status: 'ok',
      imageUrl: v.imageUrl ?? DemoHero,
    },
  ];
}

const CUSTOM_SETUP = { label: 'Custom Setup', products: 1, keywords: 2 };
const SUMMER_CAMPAIGN = { code: 'SUMMERLINEN15', label: 'SUMMERLINEN15 • 15% off' };

/**
 * Mock 排期数据，对齐 Figma（Aug 2026）。
 *
 * - Aug 4 10:00 是「生成失败」那条，点开正是 Figma 详情弹窗（Behind the Weave，Shopify Blog Hero 图超时）。
 * - 其余为正常排期条目，chip 显示各自标题；点开走同一套详情弹窗结构。
 * - Content Pool 在 Figma 上是空的（0 unscheduled）→ 这里没有 date=null 的条目。
 */
export const MOCK_CALENDAR_ITEMS: CalendarItem[] = [
  {
    id: 'behind-the-weave',
    title: 'Behind the Weave: How Our Linen is Made',
    date: '2026-08-04',
    time: '10:00',
    genCount: 3,
    setup: CUSTOM_SETUP,
    campaign: SUMMER_CAMPAIGN,
    versions: mkVersions({
      blogTitle: 'Behind the Weave: How Our Linen is Made',
      blog: 'Every Linen Wrap Dress starts in a flax field in Normandy. Flax is an undemanding crop — it grows on rainfall alone, needs no irrigation, and enriches the soil it leaves behind. That is not a marketing line; it is the reason European flax is the benchmark fibre for linen, and the reason we buy ours from three family farms we can name.\n\nFrom harvest, the straw is retted in the field — dew and time separating fibre from stalk, no chemicals involved — then scutched and combed into long, glossy line fibre. Short fibres make cheap, pilly linen; long line fibre makes the smooth, cool-handed cloth you feel in our dress. When a label says "quality linen", fibre length is most of what it is actually measuring.\n\nThe weave is where we made our own call. We commissioned a slightly denser weave than the industry default: a touch more yarn per centimetre, which costs more per metre but changes how the dress lives. The drape stays fluid instead of papery, wrinkles relax out with body heat rather than setting in by noon, and the cloth stands up to years of washing — linen is one of the rare fabrics that genuinely improves with laundering, softening without thinning.\n\nSo when you wear the Wrap Dress, you are wearing a specific set of decisions: rain-fed French flax, field retting, long-line fibre only, and a weave chosen for how it behaves at 4pm, not how it photographs at 10am. That is what "quality linen" measures — and why we expect this dress to be in your rotation for years, not seasons.',
      igTitle: 'From flax field to your wardrobe',
      ig: 'From a Normandy flax field to your wardrobe 🌾 Our linen is woven slow on purpose — softer drape, fewer wrinkles, zero irrigation. Tap to see how the Wrap Dress is made. #linen #slowfashion #behindthescenes',
    }),
  },
  {
    id: 'five-ways-to-style',
    title: '5 Ways to Style Your Linen Wrap Dress',
    date: '2026-08-06',
    time: '19:00',
    genCount: 1,
    setup: CUSTOM_SETUP,
    versions: mkVersions({
      blogTitle: '5 Ways to Style Your Linen Wrap Dress',
      blog: 'One dress, five completely different days — that is the whole promise of the Linen Wrap Dress, and this week we put it to the test with real outfits, photographed in order, no cheating between shots.\n\nMonday, the office: the dress worn straight, a structured blazer over it, loafers, hair up. The wrap silhouette reads tailored under a shoulder line, and the linen keeps you comfortable through a 2pm meeting room with no air conditioning.\n\nWednesday, sunset drinks: lose the blazer, add gold hoops and flat sandals, loosen the wrap a notch. The same dress goes from tailored to easy in ninety seconds — we timed it.\n\nSaturday morning, the beach walk: straw tote, sunglasses, barefoot with the hem catching the wind. Linen dries fast and shrugs off salt air, which is why it has dressed coastlines for a few thousand years.\n\nSaturday afternoon, the gallery: swap the tote for a slim crossbody and add white sneakers. Sunday dinner: the wrap tied tighter, heeled mules, a red lip. Five looks, one garment — and every supporting piece is something most wardrobes already own, which is exactly the point. A dress you restyle is a dress you actually wear.',
      igTitle: 'One dress, five days',
      ig: 'One dress. Five days. Zero repeats 🔁 Office → sunset drinks → beach walk → gallery → dinner. Which look is yours? Full styling guide on the blog ✨ #linendress #capsulewardrobe #styling',
    }),
  },
  {
    id: 'beach-wedding',
    title: 'Beach Wedding Guest Looks in Linen',
    date: '2026-08-11',
    time: '10:00',
    genCount: 1,
    setup: CUSTOM_SETUP,
    versions: mkVersions({
      blogTitle: 'Beach Wedding Guest Looks in Linen',
      blog: 'Thirty degrees, sand underfoot, and a dress code that says "elegant" — the beach wedding is the hardest brief in a summer wardrobe. Synthetic dresses photograph well for an hour and then cling; cotton creases and stays creased. This is exactly the assignment linen was made for.\n\nLook one: the Wrap Dress in ivory, worn with flat gold sandals and a raffia clutch. The wrap line flatters in photos from every angle, and the fabric moves with the sea breeze instead of fighting it. Ceremony to reception without a single adjustment.\n\nLook two: the same dress in sage, layered with a fine gold chain and espadrille wedges — wedges matter on sand, stilettos do not survive it. Linen\'s texture reads rich in golden-hour light, which is when every photo you will actually keep gets taken.\n\nLook three: for the guest who runs cold after sunset, add our matching linen shawl. It packs to nothing, steams smooth with a hotel kettle, and turns the outfit formal enough for the dinner tables.\n\nThe test that matters: the drive home. Linen wrinkles soft and shakes out; you arrive back looking like the evening went well — because it did. Elegant at 3pm, elegant at midnight, no outfit change required.',
      igTitle: 'Beach wedding, 30°C, covered',
      ig: 'Beach wedding at 30°C? Covered 🌊 Three linen looks that stay elegant through the ceremony, the photos and the dance floor. Save this for wedding season 📌 #weddingguest #linenlooks #summerwedding',
    }),
  },
  {
    id: 'behind-the-weave-2',
    title: 'Behind the Weave: How Our Linen is Made',
    date: '2026-08-13',
    time: '19:00',
    genCount: 2,
    setup: CUSTOM_SETUP,
    versions: mkVersions({
      blogTitle: 'Inside the Mill: How Our Linen is Made',
      blog: 'This is a photo essay from inside the mill that weaves our linen — the part of the supply chain most brands keep abstract. We think the machines, and the hands between them, are worth seeing.\n\nFirst, the scutching line: flax straw goes in, and rollers and turbines beat the woody core away from the fibre. The waste — shive — is not wasted; it heats the mill in winter. What survives is a blond ribbon of raw fibre, and here is the first of three places we pay for the slower route: the line runs at reduced speed, which breaks fewer long fibres.\n\nNext, wet spinning. The combed fibre passes through warm water just before the twist is set, which lets it spin into a finer, smoother yarn than dry spinning allows. Slower, costlier — and the single biggest reason the Wrap Dress feels the way it does against skin.\n\nThen the loom hall, and after it the step no camera makes glamorous: inspection. Every metre of our cloth crosses a lit table, and a person, not a sensor, decides what passes. Slower route number three.\n\nFlax straw to finished cloth takes about nine weeks this way. There are faster ways; we have handled what they produce, and so have you, in dresses that lasted one summer. Nine weeks is what forever feels like at the start.',
      igTitle: 'Inside the mill',
      ig: 'Inside the mill 🏭 The slow route from flax straw to the fabric of your Wrap Dress — swipe for the machines (and the hands) that make it. #madeslow #linen #craft',
    }),
  },
  {
    id: 'why-linen-beats',
    title: 'Why Linen Beats Cotton in Summer',
    date: '2026-08-18',
    time: '10:00',
    genCount: 1,
    setup: CUSTOM_SETUP,
    versions: mkVersions({
      blogTitle: 'Why Linen Beats Cotton in Summer',
      blog: "We ran linen and cotton through the same summer week — same wearer, same commute, same 30-degree afternoons — and kept notes. Here is the case for linen when it is hot, with the numbers.\n\nBreathability first: linen's hollow fibre and our open weave move air roughly twice as freely as the equivalent cotton poplin. In practice that is the difference between a fabric that ventilates and one that seals. By the 4pm test — the honest hour of any summer garment — the linen still felt dry to the touch; the cotton did not.\n\nMoisture is the second number. Linen absorbs up to a fifth of its weight in water before it feels damp, and releases it fast. Cotton absorbs even more but holds on to it, which is why a cotton shirt clings after a warm walk and a linen one recovers in minutes.\n\nThen the part nobody measures but everyone notices: how the fabric ages inside a week. Cotton creases sharp and stays creased until it meets an iron. Linen creases soft, and the folds relax with body heat — the lived-in drape that makes worn linen look intentional rather than tired.\n\nLaundry notes: cold or 30-degree wash, line dry, skip the dryer. Linen sheds wrinkles on the hanger and gains softness with every wash. The verdict of the week was not close.",
      igTitle: 'Linen vs cotton, settled',
      ig: 'Linen vs cotton in a heatwave — we tested both for a week ☀️ Spoiler: one of them was still comfortable at 4pm. The numbers are on the blog. #linenvscotton #summerfabrics #stayscool',
    }),
  },
  {
    id: 'brunch-to-beach',
    title: 'From Brunch to Beach: One Dress',
    date: '2026-08-20',
    time: '19:00',
    genCount: 1,
    setup: CUSTOM_SETUP,
    versions: mkVersions({
      blogTitle: 'From Brunch to Beach: One Dress',
      blog: 'One Linen Wrap Dress, two very different afternoons — a city brunch at eleven, a beach at four — and a single tote of swaps to bridge them. Here is the exact change list, and why the dress works as the constant.\n\nBrunch: the dress worn with the wrap tied high, a fine belt, ballet flats and a structured little bag. Linen at brunch reads polished, not precious — the texture does the work a print would usually do, so accessories stay quiet.\n\nThe tote carried: flat sandals, a straw hat, sunglasses, a swimsuit, and a cotton scrunchie. Total swap time in a car park: under three minutes. Belt off, flats to sandals, hat on, hair up. The bag rides inside the tote.\n\nThe beach: the same dress, now loose over a swimsuit, doing the job of the cover-up you did not have to pack. This is the quiet advantage of a wrap silhouette — it adjusts from fitted to easy without looking like a compromise in either mode, and linen handles sand, salt air and a damp swimsuit underneath without complaint.\n\nThe drive home is the epilogue: shake the dress out, retie the wrap, and dinner is possible without going home first. One garment, one tote, an entire Saturday — that is what cost-per-wear actually looks like.',
      igTitle: 'Brunch → beach, one dress',
      ig: 'Brunch → beach with one dress and one tote 👜 Swipe for the exact swap list. Bet you own most of it already 😉 #onedress #brunchtobeach #summerstyle',
    }),
  },
  {
    id: 'sustainable-fabric',
    title: 'Sustainable Fabric, Effortless Style',
    date: '2026-08-25',
    time: '10:00',
    genCount: 1,
    setup: CUSTOM_SETUP,
    versions: mkVersions({
      blogTitle: 'Sustainable Fabric, Effortless Style',
      blog: '"Sustainable" is the most overused word on a clothing label, so let us be specific about what it means on ours.\n\nStart with the crop. European flax is rain-fed — no irrigation systems, in an industry where a single conventional cotton shirt can carry a four-figure litre water footprint. The plant needs minimal intervention, flowers for a single morning, and improves the soil for whatever is planted after it. Every part of the harvest is used: fibre for cloth, seeds for oil, shive for fuel.\n\nThe certifications, decoded. European Flax® guarantees the fibre\'s origin and its zero-irrigation, zero-GMO cultivation. OEKO-TEX Standard 100 certifies that the finished fabric — dyes included — is free of harmful substances at the level of the thing that touches your skin. Neither is a marketing badge; both are audited, and both cost us something to keep.\n\nEnd of life is the part fashion prefers not to discuss. Undyed linen is fully biodegradable — buried, it is gone in a matter of months. But the greenest garment is the one that stays in use, and this is where durability is the real environmental feature: a dress that survives two hundred washes does not need a replacement manufactured.\n\nLow impact going in, long life in the middle, clean exit at the end. That is the whole argument — and none of it required the word "eco".',
      igTitle: 'Low impact, high style',
      ig: 'Rain-fed. Biodegradable. Outlasts trends 🌱 What "sustainable linen" actually means on our labels — full breakdown on the blog. #sustainablefashion #europeanflax #wearbetter',
    }),
  },
  {
    id: 'styling-the-aurora',
    title: 'Styling the Aurora Silk Scarf',
    date: '2026-08-27',
    time: '19:00',
    genCount: 1,
    setup: { label: 'Custom Setup', products: 1, keywords: 2 },
    versions: mkVersions({
      blogTitle: 'Styling the Aurora Silk Scarf',
      blog: 'The Aurora Silk Scarf is the smallest thing we make and the hardest working. Three ways to wear it this season — with the knot diagrams, because the knot is the whole trick.\n\nIn your hair, for the beach: fold the scarf into a band, place it at the hairline, tie under at the nape and let the tails hang. Silk holds against wind and salt spray without crushing your hair the way elastic does, and the Aurora print reads like intention rather than damage control on day-two beach hair.\n\nOn the bag, for the commute: loop it twice around the strap of a plain tote and finish with a single flat knot at the base. It is the fastest possible upgrade to a work bag — thirty seconds — and it keeps the scarf where you can retrieve it when the office air conditioning wins.\n\nOver the shoulders, for dinner: the finishing-layer move. Drape it over the Wrap Dress, tie a loose square knot low at the sternum, and let the points fall asymmetric. Silk against linen is a texture pairing that photographs richer than either fabric alone.\n\nOne scarf, three registers — beach, commute, dinner. The diagrams for each knot are below, and every one is easier done than described.',
      igTitle: 'Three ways to Aurora',
      ig: 'Hair. Bag. Shoulders. The Aurora Silk Scarf does all three 🧣 Swipe for the knot diagrams — save for later 📌 #silkscarf #accessorize #threeways',
    }),
  },
];
